import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Subscription } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';
import { ProductsService } from '@app/services/products/product.service';

interface SerialRow { serialNumber: string; receivedAt: string | null; }
interface SerialGroup {
  key: string;
  name: string;
  path: string;
  rows: SerialRow[];
}

// Read-only list of a product's in-stock serial numbers grouped by storage.
@Component({
  selector: 'app-serial-stock',
  templateUrl: './serial-stock.component.html',
  styleUrls: ['./serial-stock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SerialStockComponent implements OnChanges, OnDestroy {
  @Input() prdID: number;
  @Input() productName: string | null | undefined;

  readonly pageSize = 200;
  readonly step = 25;
  readonly filterFrom = 8;

  loading = true;
  error = false;
  groups: SerialGroup[] = [];
  total = 0;
  filter = '';
  expanded = new Set<string>();
  shown: { [key: string]: number } = {};
  private _sub: Subscription;

  constructor(private _products: ProductsService, private _router: Router, private _cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.prdID && this.prdID) { this.load(); }
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  load(): void {
    this._sub?.unsubscribe();
    this.loading = true;
    this.error = false;
    let page = 0;
    this._sub = this._products.getSerialNumbersInStock(this.prdID, page, this.pageSize).pipe(
      expand(res => {
        page++;
        return (res?.serialNumbers?.length || 0) >= this.pageSize && page * this.pageSize < (res.total || 0)
          ? this._products.getSerialNumbersInStock(this.prdID, page, this.pageSize)
          : EMPTY;
      }),
      reduce((all: any[], res: any) => all.concat(res?.serialNumbers || []), [])
    ).subscribe({
      next: rows => {
        this.build(rows);
        this.loading = false;
        this._cdr.markForCheck();
      },
      error: () => {
        this.error = true;
        this.loading = false;
        this._cdr.markForCheck();
      }
    });
  }

  private build(rows: any[]): void {
    const map = new Map<string, SerialGroup>();
    for (const r of rows) {
      const key = r.storageStructureID != null ? String(r.storageStructureID) : 'none';
      let g = map.get(key);
      if (!g) {
        const path: string = r.storagePath || '';
        const parts = path.split('->').map(p => p.trim()).filter(p => p);
        g = {
          key,
          name: parts.length ? parts[parts.length - 1] : '',
          path: parts.length > 1 ? parts.slice(0, -1).join(' > ') : '',
          rows: []
        };
        map.set(key, g);
      }
      g.rows.push({ serialNumber: r.serialNumber, receivedAt: r.receivedAt });
    }
    this.groups = Array.from(map.values());
    this.total = rows.length;
    this.expanded.clear();
    this.shown = {};
    if (this.groups.length === 1) { this.expanded.add(this.groups[0].key); }
  }

  get term(): string { return this.filter.trim().toLowerCase(); }

  matches(g: SerialGroup): SerialRow[] {
    const t = this.term;
    return t ? g.rows.filter(r => r.serialNumber.toLowerCase().includes(t)) : g.rows;
  }

  get visibleGroups(): { group: SerialGroup; rows: SerialRow[] }[] {
    return this.groups
      .map(group => ({ group, rows: this.matches(group) }))
      .filter(x => x.rows.length > 0);
  }

  get matchTotal(): number {
    return this.visibleGroups.reduce((n, x) => n + x.rows.length, 0);
  }

  isOpen(g: SerialGroup): boolean { return !!this.term || this.expanded.has(g.key); }

  toggle(g: SerialGroup): void {
    if (this.expanded.has(g.key)) { this.expanded.delete(g.key); } else { this.expanded.add(g.key); }
  }

  expandAll(open: boolean): void {
    this.expanded.clear();
    if (open) { this.groups.forEach(g => this.expanded.add(g.key)); }
  }

  get allOpen(): boolean { return this.groups.length > 0 && this.expanded.size === this.groups.length; }

  limit(g: SerialGroup): number { return this.shown[g.key] || this.step; }

  more(g: SerialGroup): void { this.shown[g.key] = this.limit(g) + this.step; }

  highlight(value: string): { pre: string; hit: string; post: string } {
    const t = this.term;
    const i = t ? value.toLowerCase().indexOf(t) : -1;
    return i < 0
      ? { pre: value, hit: '', post: '' }
      : { pre: value.slice(0, i), hit: value.slice(i, i + t.length), post: value.slice(i + t.length) };
  }

  openReport(event: Event): void {
    event.preventDefault();
    this._router.navigate(['/reports/serialized'], {
      queryParams: { productId: this.prdID, ...(this.productName ? { productName: this.productName } : {}) }
    });
  }
}
